import axios from 'axios';

import { API_KEY } from '../config/config';

const api = axios.create({
  baseURL: API_KEY,
});

// Anexa Authorization em toda request.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err),
);

// Uma única chamada de refresh por vez. Se várias requisições receberem
// 401 ao mesmo tempo (comum após access token expirar), todas esperam
// o mesmo Promise em vez de dispararem N refreshes concorrentes.
let refreshPromise = null;

const performRefresh = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('missing refresh token');
  }

  const { data } = await axios.post(
    `${API_KEY}/auth/refresh`,
    { refresh_token: refreshToken },
  );

  localStorage.setItem('token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);
  return data.access_token;
};

const redirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  // Hard redirect — evita depender de instância de router aqui.
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

/* eslint-disable no-underscore-dangle, no-param-reassign */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Sem response = network error, propaga.
    if (!error.response) return Promise.reject(error);

    const is401 = error.response.status === 401;
    const url = originalRequest?.url || '';
    // Rotas de autenticação não podem tentar refresh — /login falhado
    // é senha errada, /refresh falhado é token morto, /app-bootstrap
    // falhado é chave errada. Todas devem propagar o 401 pra UI tratar.
    const isAuthCall = url.includes('/auth/login')
      || url.includes('/auth/refresh')
      || url.includes('/auth/app-bootstrap');
    const alreadyRetried = originalRequest?._retry;

    if (is401 && url.includes('/auth/refresh')) {
      // Refresh próprio falhou → refresh token morreu, sessão fim.
      redirectToLogin();
      return Promise.reject(error);
    }

    if (is401 && !alreadyRetried && !isAuthCall) {
      originalRequest._retry = true;

      try {
        if (!refreshPromise) {
          refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccess = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return api(originalRequest);
      } catch (refreshErr) {
        redirectToLogin();
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
